import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RecipeForm from '../components/RecipeForm';
import PageLayout from '../components/PageLayout';

function AddRecipe() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('import');
  const [url, setUrl] = useState('');
  const [importedRecipe, setImportedRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleManualSubmit = async (formData) => {
    try {
      console.log('Submitting recipe data:', formData);
      const response = await axios.post(`/api/recipes`, formData);
      console.log('Recipe saved successfully:', response.data);
      navigate('/recipes');
    } catch (error) {
      console.error('Error creating recipe:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create recipe. Please try again.';
      alert(`Failed to create recipe: ${errorMessage}`);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`/api/recipes/import`, { url });
      setImportedRecipe(response.data);
    } catch (err) {
      console.error('Error importing recipe:', err);
      setError(err.response?.data?.error || 'Failed to import recipe. Please try another URL or switch to manual entry.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveImportedRecipe = async (formData) => {
    try {
      console.log('Submitting recipe data:', formData);
      const response = await axios.post(`/api/recipes`, formData);
      console.log('Recipe saved successfully:', response.data);
      navigate(`/recipes/${response.data._id}`);
    } catch (error) {
      console.error('Error saving recipe:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save recipe. Please try again.';
      alert(`Failed to save recipe: ${errorMessage}`);
    }
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    // Reset import-related state when switching modes
    if (newMode === 'manual') {
      setImportedRecipe(null);
      setUrl('');
      setError('');
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/add-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          minHeight: '100vh',
          height: '100%'
        }}
      />
      <PageLayout>
        <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Add Recipe</h1>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
          {/* Toggle Switch */}
          <div className="mb-8">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleModeChange('manual')}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                  mode === 'manual'
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-gray-900 bg-gray-100'
                }`}
              >
                Add Manually
              </button>
              <button
                onClick={() => handleModeChange('import')}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                  mode === 'import'
                    ? 'bg-sky-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-gray-900 bg-gray-100'
                }`}
              >
                Import from URL
              </button>
            </div>
          </div>

          {/* Content based on mode */}
          {mode === 'manual' ? (
            <div>
              {!importedRecipe ? (
                <RecipeForm onSubmit={handleManualSubmit} onCancel={() => navigate('/recipes')} />
              ) : (
                <div>
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
                    <p className="text-green-800 text-sm mb-4">
                      ✓ Recipe imported successfully! Please review and edit the information below before saving.
                    </p>
                    <button
                      onClick={() => {
                        setImportedRecipe(null);
                        handleModeChange('manual');
                      }}
                      className="text-sm text-green-800 underline hover:text-green-900"
                    >
                      Start over with empty form
                    </button>
                  </div>
                  <RecipeForm 
                    recipe={importedRecipe} 
                    onSubmit={handleManualSubmit} 
                    onCancel={() => navigate('/recipes')}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              {!importedRecipe ? (
                <div>
                  <p className="text-gray-600 mb-6">
                    Enter a recipe URL to automatically import all the recipe information. 
                    This works best with websites that use structured data (Schema.org Recipe markup).
                  </p>

                  <form onSubmit={handleImport} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Recipe URL</label>
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://www.example.com/recipe"
                        required
                        className="w-full p-3 border border-gray-300 rounded"
                      />
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                        <br />
                        <span className="text-sm">
                          You can switch to manual entry using the toggle above.
                        </span>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
                      >
                        {loading ? 'Importing...' : 'Import Recipe'}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/recipes')}
                        className="px-6 py-3 bg-stone-600 text-white rounded-lg font-semibold hover:bg-stone-700 transition-colors shadow-md"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>

                  <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded">
                    <h3 className="font-bold text-blue-900 mb-2">💡 Tip</h3>
                    <p className="text-sm text-blue-800">
                      Works great with recipe sites like AllRecipes, Food Network, BBC Good Food, 
                      and many others that use structured recipe data. After importing, you can 
                      review and edit the information before saving.
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
                    <p className="text-green-800 text-sm mb-4">
                      ✓ Recipe imported successfully! Please review the information below and make any necessary changes before saving.
                    </p>
                    <button
                      onClick={() => {
                        setImportedRecipe(null);
                        setUrl('');
                        setError('');
                      }}
                      className="text-sm text-green-800 underline hover:text-green-900"
                    >
                      Import another recipe
                    </button>
                  </div>

                  <RecipeForm 
                    recipe={importedRecipe} 
                    onSubmit={handleSaveImportedRecipe} 
                    onCancel={() => navigate('/recipes')}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </PageLayout>
    </>
  );
}

export default AddRecipe;